<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import FinanceiroAPI from 'dashboard/api/financeiro';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import LeasesAPI from 'dashboard/api/leases';
import { useAlert } from 'dashboard/composables';
import LeaseOperationPanel from 'dashboard/routes/dashboard/leases/components/LeaseOperationPanel.vue';
import FinancingSimulator from '../components/FinancingSimulator.vue';
import FinanceiroBoletoModal from '../components/FinanceiroBoletoModal.vue';
import FinanceiroBoletosPanel from '../components/FinanceiroBoletosPanel.vue';
import FinanceiroClosePeriodModal from '../components/FinanceiroClosePeriodModal.vue';
import FinanceiroCobrancaPanel from '../components/FinanceiroCobrancaPanel.vue';
import FinanceiroContratadosPanel from '../components/FinanceiroContratadosPanel.vue';
import FinanceiroHistoricoPanel from '../components/FinanceiroHistoricoPanel.vue';
import FinanceiroOfferActionModal from '../components/FinanceiroOfferActionModal.vue';
import FinanceiroTabState from '../components/FinanceiroTabState.vue';
import FinanceiroTabsShell from '../components/FinanceiroTabsShell.vue';
import ComissoesIndex from './ComissoesIndex.vue';

const route = useRoute();
const router = useRouter();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const routeNumber = (value, fallback) => {
  const parsed = Number(routeString(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const normalizeMainTab = value =>
  [
    'contratados',
    'cobranca',
    'boletos',
    'historico',
    'propostas',
    'locacoes',
    'comissoes',
  ].includes(value)
    ? value
    : 'contratados';
const normalizeOfferTab = value =>
  ['negotiation', 'accepted', 'canceled'].includes(value)
    ? value
    : 'negotiation';

const FINANCEIRO_TABS = Object.freeze([
  { id: 'contratados', label: 'Plano', icon: 'i-lucide-badge-dollar-sign' },
  { id: 'cobranca', label: 'Cobrança', icon: 'i-lucide-gauge' },
  { id: 'boletos', label: 'Boletos', icon: 'i-lucide-receipt' },
  { id: 'historico', label: 'Histórico', icon: 'i-lucide-history' },
  { id: 'propostas', label: 'Propostas', icon: 'i-lucide-handshake' },
  { id: 'locacoes', label: 'Locações', icon: 'i-lucide-key-round' },
  { id: 'comissoes', label: 'Comissões', icon: 'i-lucide-badge-percent' },
]);

const tab = ref(normalizeMainTab(routeString(route.query.tab)));
// SPEC 32 / P37-7 — declarado cedo: usado por syncFinanceiroRouteState.
const cobrancaCiclo = ref(
  routeString(route.query.ciclo) === 'anual' ? 'anual' : 'mensal'
);
const loading = ref(false);
const resumoLoading = ref(false);
const cobrancasLoading = ref(false);
const historicoLoading = ref(false);
const tabErrors = ref({
  contratados: '',
  cobranca: '',
  boletos: '',
  historico: '',
  propostas: '',
  locacoes: '',
});
const historyFilters = ref({
  start_date: routeString(route.query.start_date) || '',
  end_date: routeString(route.query.end_date) || '',
  transaction_kind: routeString(route.query.transaction_kind) || '',
  public_kind: routeString(route.query.public_kind) || '',
  q: routeString(route.query.history_q) || '',
  limit: routeNumber(route.query.limit, 50),
});
const offerTab = ref(
  normalizeOfferTab(
    routeString(route.query.offer_status || route.query.offer_tab)
  )
);
const offerLoading = ref(false);
const offers = ref([]);
const offerMeta = ref({ count: 0 });
const leases = ref([]);
const leasesLoading = ref(false);
const selectedLeaseId = ref('');
const pendingOfferAction = ref(null);
const showClosePeriodModal = ref(false);
const closingPeriod = ref(false);
const creditAnalysisLoading = ref(false);
const offerFilters = ref({
  business_context: routeString(route.query.business_context) || '',
  kind: routeString(route.query.kind) || '',
  q: routeString(route.query.q) || '',
});

const proximaCobranca = ref({
  status: 'indisponivel',
  valor: null,
  vencimento: null,
});
const planoAtual = ref({
  nome: '-',
  preco: null,
  atendimentos_ciclo: 0,
});
const usoCiclo = ref({
  atendimentos: 0,
  limite: 0,
  percentual: 0,
  invalidos: 0,
  trigger_kinds: {},
  invalidos_por_motivo: {},
  janela_dias: 30,
});
const cobrancas = ref([]);
const historico = ref({
  periodo: { label: '-' },
  cards: {
    saldo: null,
    receita: null,
    despesa: null,
    comissao: null,
  },
  serie_mensal: [],
  transacoes: [],
  historico_publico: [],
  atividades: [],
  resumo_publico: {
    total: 0,
    tickets: 0,
    feedback_suporte: 0,
    sessoes_responde: 0,
    visitas_publicas: 0,
    incidentes_status: 0,
  },
});

const STATUS = {
  pago: { label: 'Pago', cls: 'text-emerald-700 bg-emerald-50' },
  aberto: { label: 'Em aberto', cls: 'text-amber-700 bg-amber-50' },
  atrasado: { label: 'Atrasado', cls: 'text-rose-700 bg-rose-50' },
  processado: { label: 'Processado', cls: 'text-sky-700 bg-sky-50' },
  submitted: { label: 'Proposta recebida', cls: 'text-sky-700 bg-sky-50' },
  accepted: { label: 'Proposta aceita', cls: 'text-emerald-700 bg-emerald-50' },
  withdrawn: {
    label: 'Proposta cancelada',
    cls: 'text-slate-700 bg-slate-100',
  },
  in_progress: { label: 'Em andamento', cls: 'text-amber-700 bg-amber-50' },
  waiting_on_customer: {
    label: 'Aguardando cliente',
    cls: 'text-amber-700 bg-amber-50',
  },
  resolved: { label: 'Resolvido', cls: 'text-emerald-700 bg-emerald-50' },
  active: { label: 'Ativa', cls: 'text-emerald-700 bg-emerald-50' },
  reset: { label: 'Reiniciada', cls: 'text-slate-700 bg-slate-100' },
  pending: { label: 'Pendente', cls: 'text-amber-700 bg-amber-50' },
  confirmed: { label: 'Confirmada', cls: 'text-emerald-700 bg-emerald-50' },
  captured: { label: 'Capturado', cls: 'text-emerald-700 bg-emerald-50' },
  estornado: { label: 'Estornado', cls: 'text-slate-700 bg-slate-100' },
  falhou: { label: 'Falhou', cls: 'text-rose-700 bg-rose-50' },
  divergente: { label: 'Divergente', cls: 'text-rose-700 bg-rose-50' },
  reconciled: { label: 'Reconciliada', cls: 'text-emerald-700 bg-emerald-50' },
  divergent: { label: 'Divergente', cls: 'text-rose-700 bg-rose-50' },
  cancelado: { label: 'Cancelado', cls: 'text-slate-700 bg-slate-100' },
  declined: { label: 'Proposta cancelada', cls: 'text-rose-700 bg-rose-50' },
  capturado: { label: 'Capturado', cls: 'text-slate-700 bg-slate-100' },
  investigating: { label: 'Investigando', cls: 'text-rose-700 bg-rose-50' },
  monitoring: { label: 'Monitorando', cls: 'text-amber-700 bg-amber-50' },
};

const OFFER_BUCKETS = [
  {
    value: 'negotiation',
    label: 'Recebidas',
    title: 'Propostas recebidas',
    helper:
      'Quando você tiver propostas recebidas, vai poder acompanhá-las aqui.',
  },
  {
    value: 'accepted',
    label: 'Aceitas',
    title: 'Propostas aceitas',
    helper:
      'Quando você tiver propostas aceitas, vai poder acompanhá-las aqui.',
  },
  {
    value: 'canceled',
    label: 'Canceladas',
    title: 'Propostas canceladas',
    helper:
      'Quando você tiver propostas canceladas, vai poder acompanhá-las aqui.',
  },
];

const PAYMENT_KIND_LABELS = {
  a_vista: 'À vista',
  financiamento: 'Financiado',
  a_vista_fgts: 'À vista + FGTS',
  financiamento_fgts: 'Financiado + FGTS',
};

const BUSINESS_CONTEXT_LABELS = {
  purchase: 'Venda',
  rental: 'Aluguel',
};

const OFFER_KIND_LABELS = {
  offer: 'Proposta',
  booking: 'Reserva',
};

const CREDIT_STATUS = {
  pendente: { label: 'Pendente', cls: 'text-amber-700 bg-amber-50' },
  em_analise: { label: 'Em análise', cls: 'text-sky-700 bg-sky-50' },
  pre_aprovado: {
    label: 'Pré-aprovado',
    cls: 'text-emerald-700 bg-emerald-50',
  },
  aprovado: { label: 'Aprovado', cls: 'text-emerald-700 bg-emerald-50' },
  reprovado: { label: 'Reprovado', cls: 'text-rose-700 bg-rose-50' },
};

const OFFER_ACTIONS = {
  accept: {
    title: 'Aceitar proposta',
    confirmLabel: 'Aceitar proposta',
    description:
      'A proposta será movida para a lista de propostas aceitas para acompanhamento dos próximos passos.',
    success:
      'Proposta aceita com sucesso! Acompanhe os próximos passos na lista de propostas aceitas.',
    payload: {
      notes: 'Proposta aceita pela Imudei.',
    },
    nextTab: 'accepted',
  },
  decline: {
    title: 'Recusar proposta',
    confirmLabel: 'Recusar proposta',
    description:
      'A proposta será registrada como cancelada e continuará disponível para consulta no histórico.',
    success:
      'Proposta recusada. Veja a lista de propostas canceladas para acompanhar.',
    payload: {
      notes: 'Proprietário recusou a proposta pela Imudei.',
      reason: 'owner_declined',
      reason_label: 'Proprietário recusou a proposta',
    },
    nextTab: 'canceled',
  },
  withdraw: {
    title: 'Retirar proposta',
    confirmLabel: 'Retirar proposta',
    description:
      'A proposta será marcada como retirada e a imobiliária continuará com o registro da negociação.',
    success: 'Proposta retirada. A imobiliária foi avisada.',
    payload: {
      notes: 'Interessado desistiu da proposta pela Imudei.',
      reason: 'customer_withdrew',
      reason_label: 'Interessado desistiu da proposta',
    },
    nextTab: 'canceled',
  },
};

const HISTORY_KIND = {
  receita: {
    label: 'Receita',
    cls: 'text-emerald-700 bg-emerald-50',
    icon: 'i-lucide-arrow-down-left',
  },
  despesa: {
    label: 'Despesa',
    cls: 'text-rose-700 bg-rose-50',
    icon: 'i-lucide-arrow-up-right',
  },
  comissao: {
    label: 'Comissão',
    cls: 'text-violet-700 bg-violet-50',
    icon: 'i-lucide-badge-percent',
  },
  ticket: {
    label: 'Suporte',
    cls: 'text-sky-700 bg-sky-50',
    icon: 'i-lucide-life-buoy',
  },
  feedback: {
    label: 'Feedback',
    cls: 'text-slate-700 bg-slate-100',
    icon: 'i-lucide-smile',
  },
  responde: {
    label: 'Responde',
    cls: 'text-violet-700 bg-violet-50',
    icon: 'i-lucide-message-circle',
  },
  visita: {
    label: 'Visita',
    cls: 'text-amber-700 bg-amber-50',
    icon: 'i-lucide-door-open',
  },
  status: {
    label: 'Status',
    cls: 'text-slate-700 bg-slate-100',
    icon: 'i-lucide-activity',
  },
};

const HISTORY_CARD_COPY = {
  saldo: 'Saldo',
  receita: 'Receita',
  despesa: 'Despesa',
  comissao: 'Comissão',
};

const formatBRL = value =>
  value == null
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value));

const parseDate = value => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split('/');
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }

  if (/^\d{2}\/\d{4}$/.test(raw)) {
    const [month, year] = raw.split('/');
    return new Date(`${year}-${month}-01T00:00:00`);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateOnly = value => {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleDateString('pt-BR') : value || '-';
};

const formatDateTime = value => {
  const parsed = parseDate(value);
  return parsed
    ? parsed.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : value || '-';
};

const isPeriodClosed = computed(() => Boolean(historico.value.periodo?.closed));
const periodClosedCopy = computed(() => {
  if (!isPeriodClosed.value) return '';

  const closedAt = historico.value.periodo?.closed_at
    ? formatDateTime(historico.value.periodo.closed_at)
    : '';
  const closedBy = historico.value.periodo?.closed_by_name || 'Sistema';
  return `Fechado por ${closedBy}${closedAt ? ` em ${closedAt}` : ''}.`;
});

const statusMeta = status =>
  STATUS[status] || {
    label: status ? String(status).replaceAll('_', ' ') : 'Indisponível',
    cls: 'text-slate-700 bg-slate-100',
  };

const currentOfferBucket = computed(
  () =>
    OFFER_BUCKETS.find(bucket => bucket.value === offerTab.value) ||
    OFFER_BUCKETS[0]
);

const offerSummary = computed(() => ({
  count: offerMeta.value.count || offers.value.length,
  amount: offers.value.reduce(
    (sum, offer) => sum + Number(offer.offer_amount || 0),
    0
  ),
}));

const offerStatusParams = () => {
  if (offerTab.value === 'negotiation') return { status: 'submitted' };
  if (offerTab.value === 'accepted') return { status: 'accepted' };
  return { status_bucket: 'canceled' };
};

const cleanParams = params =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value != null)
  );

const paymentKindLabel = value => PAYMENT_KIND_LABELS[value] || 'Não informado';
const businessContextLabel = value =>
  BUSINESS_CONTEXT_LABELS[value] || 'Indisponível';
const offerKindLabel = value => OFFER_KIND_LABELS[value] || 'Proposta';

const offerPersonLabel = offer =>
  offer.contact_name ||
  offer.metadata?.requester_name ||
  offer.contact_phone_number ||
  offer.contact_email ||
  'Cliente da proposta';

const offerDateLabel = offer =>
  offer.decided_at
    ? `Atualizada em ${formatDateOnly(offer.decided_at)}`
    : `Recebida em ${formatDateOnly(offer.submitted_at)}`;

const offerProgressLabel = offer => {
  if (offer.status === 'submitted') return 'Proposta recebida';
  if (offer.status === 'accepted') return 'Proposta aceita';
  if (offer.status === 'withdrawn') return 'Proposta retirada';
  return 'Proposta cancelada';
};

const offerNextStep = offer => {
  if (offer.status === 'submitted') {
    return 'Avalie o valor, a forma de pagamento e as observações antes de aceitar ou recusar.';
  }

  if (offer.status === 'accepted') {
    return 'Informe os valores finais da transação, documentos e próximos passos.';
  }

  return (
    offer.metadata?.decision?.reason_label ||
    'Veja a lista para acompanhar as atualizações.'
  );
};

const creditAnalysisFor = offer =>
  offer.credit_analysis || offer.metadata?.credit_analysis || {};

const hasCreditAnalysis = offer =>
  Object.keys(creditAnalysisFor(offer)).length > 0;

const creditStatusMeta = offer => {
  const status = creditAnalysisFor(offer).status || 'pendente';
  return (
    CREDIT_STATUS[status] || {
      label: status.replaceAll('_', ' '),
      cls: 'text-slate-700 bg-slate-100',
    }
  );
};

const creditIncomeLabel = offer => {
  const analysis = creditAnalysisFor(offer);
  if (analysis.income_commitment == null) return 'Renda não informada';

  const commitment = Math.round(Number(analysis.income_commitment) * 100);
  return analysis.income_compatible === false
    ? `Renda acima do limite (${commitment}%)`
    : `Renda compatível (${commitment}%)`;
};

const historyKindMeta = kind =>
  HISTORY_KIND[kind] || {
    label: kind || 'Evento',
    cls: 'text-slate-700 bg-slate-100',
    icon: 'i-lucide-circle',
  };

const baixarBoleto = url => {
  if (!url) return;
  window.open(url, '_blank', 'noopener');
};

const copiarCodigo = async codigo => {
  if (!codigo) return;
  try {
    await navigator.clipboard.writeText(codigo);
    useAlert('Código copiado com sucesso');
  } catch {
    useAlert('Não foi possível copiar o código');
  }
};

const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

const syncFinanceiroRouteState = () => {
  const nextQuery = compactQuery({
    ...route.query,
    tab: tab.value === 'contratados' ? undefined : tab.value,
    lease_id:
      tab.value === 'locacoes' ? selectedLeaseId.value || undefined : undefined,
    ciclo: cobrancaCiclo.value === 'mensal' ? undefined : cobrancaCiclo.value,
    offer_status: offerTab.value === 'negotiation' ? undefined : offerTab.value,
    business_context: offerFilters.value.business_context || undefined,
    kind: offerFilters.value.kind || undefined,
    q: offerFilters.value.q || undefined,
    start_date: historyFilters.value.start_date || undefined,
    end_date: historyFilters.value.end_date || undefined,
    transaction_kind: historyFilters.value.transaction_kind || undefined,
    public_kind: historyFilters.value.public_kind || undefined,
    history_q: historyFilters.value.q || undefined,
    limit:
      Number(historyFilters.value.limit || 50) === 50
        ? undefined
        : historyFilters.value.limit,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

const setTab = nextTab => {
  tab.value = normalizeMainTab(nextTab);
  syncFinanceiroRouteState();
};

watch(
  () => route.query.tab,
  value => {
    const nextTab = normalizeMainTab(routeString(value));
    if (nextTab !== tab.value) {
      tab.value = nextTab;
    }
  }
);

const selectLease = lease => {
  selectedLeaseId.value = lease.id;
  syncFinanceiroRouteState();
};

const loadResumo = async () => {
  resumoLoading.value = true;
  tabErrors.value.contratados = '';
  try {
    const { data } = await FinanceiroAPI.resumo();
    proximaCobranca.value = data.proxima_cobranca;
    planoAtual.value = data.plano_atual;
    usoCiclo.value = data.uso_ciclo;
  } catch {
    tabErrors.value.contratados =
      'Não foi possível carregar o resumo financeiro.';
    useAlert('Não foi possível carregar o resumo financeiro.');
  } finally {
    resumoLoading.value = false;
  }
};

const usageOriginEntries = computed(() =>
  Object.entries(usoCiclo.value.trigger_kinds || {}).filter(
    ([, value]) => Number(value) > 0
  )
);

const invalidReasonEntries = computed(() =>
  Object.entries(usoCiclo.value.invalidos_por_motivo || {}).filter(
    ([, value]) => Number(value) > 0
  )
);

const triggerKindLabel = value =>
  ({
    organic: 'Orgânico',
    form_auto_start: 'Formulário',
    remarketing: 'Remarketing',
    ad_update: 'Atualização de anúncio',
    manual: 'Manual',
    desconhecido: 'Sem origem',
  })[value] || value;

const invalidReasonLabel = value =>
  ({
    invalid_whatsapp: 'WhatsApp inválido',
    meta_error: 'Erro Meta',
    bypass_origin: 'Origem sem cobrança',
    invalid_contact: 'Contato inválido',
    desconhecido: 'Sem motivo',
  })[value] || value;

const loadCobrancas = async () => {
  cobrancasLoading.value = true;
  tabErrors.value.boletos = '';
  try {
    const { data } = await FinanceiroAPI.cobrancas();
    cobrancas.value = data.data || [];
  } catch {
    tabErrors.value.boletos = 'Não foi possível carregar as cobranças.';
    useAlert('Não foi possível carregar as cobranças.');
  } finally {
    cobrancasLoading.value = false;
  }
};

// SPEC 32 / P37-7 — Cobrança por atendimento (cota/consumo/boleto)
const cobranca = ref({
  billing_source: 'manual',
  ciclo: 'mensal',
  contrato: {
    atendimentos_contratados: 0,
    cota_mensal: 0,
    preco_por_atendimento: 0,
    inicio_contrato: null,
    valor_contrato: 0,
  },
  uso: {
    consumidos_mes: 0,
    cota_mensal: 0,
    percentual: 0,
    near_limit: false,
    overage: 0,
    overage_valor: 0,
    janela_dias: 30,
  },
  boleto_aberto: null,
  historico_boletos: [],
});
const cobrancaLoading = ref(false);
const boletoConsultaLoading = ref(false);
const boletoEmptyNotice = ref('');
const boletoModal = ref(null);
const boletoCopiado = ref(false);
const attendanceWindowDays = computed(
  () => cobranca.value.uso?.janela_dias || usoCiclo.value.janela_dias || 30
);
const attendanceWindowHelper = computed(
  () =>
    `Cada atendimento considera uma janela de ${attendanceWindowDays.value} dias corridos. Interações dentro da janela contam uma vez; remarketing e atualização de anúncios abrem uma nova janela.`
);

const cobrancaTemContrato = computed(
  () => Number(cobranca.value.contrato?.atendimentos_contratados || 0) > 0
);
const cobrancaSemConfig = computed(
  () => cobranca.value.billing_source === 'manual' && !cobrancaTemContrato.value
);
const gaugePercent = computed(() =>
  Math.min(Math.round(Number(cobranca.value.uso?.percentual || 0)), 100)
);
const gaugeColor = computed(() => {
  const pct = Number(cobranca.value.uso?.percentual || 0);
  if (Number(cobranca.value.uso?.overage || 0) > 0 || pct > 100)
    return 'bg-n-ruby-9';
  if (pct >= 80) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
});

const loadCobranca = async () => {
  cobrancaLoading.value = true;
  tabErrors.value.cobranca = '';
  try {
    const { data } = await FinanceiroAPI.cobranca({
      ciclo: cobrancaCiclo.value,
    });
    cobranca.value = data;
    boletoEmptyNotice.value = '';
  } catch {
    tabErrors.value.cobranca = 'Não foi possível carregar a cobrança.';
    useAlert('Não foi possível carregar a cobrança.');
  } finally {
    cobrancaLoading.value = false;
  }
};

const setCiclo = ciclo => {
  cobrancaCiclo.value = ciclo === 'anual' ? 'anual' : 'mensal';
  syncFinanceiroRouteState();
  loadCobranca();
};

const boletoChargeId = boleto => boleto?.charge_id || boleto?.id || boleto?.mes;

const consultBoletoAberto = async boleto => {
  if (!boleto) {
    boletoEmptyNotice.value =
      'Nenhum boleto em aberto persistido para esta cobrança.';
    return;
  }

  boletoConsultaLoading.value = true;
  boletoCopiado.value = false;
  boletoEmptyNotice.value = '';

  try {
    const { data, empty, emptyReason } =
      await FinanceiroAPI.consultarBoletoAberto({
        charge_id: boletoChargeId(boleto),
      });

    if (emptyReason === 'sem_boleto_em_aberto' || empty || !data) {
      boletoModal.value = null;
      cobranca.value = {
        ...cobranca.value,
        boleto_aberto: null,
      };
      boletoEmptyNotice.value =
        'Nenhum boleto em aberto persistido para esta cobrança.';
      return;
    }

    boletoModal.value = data;
  } catch {
    useAlert('Não foi possível consultar o boleto em aberto.');
  } finally {
    boletoConsultaLoading.value = false;
  }
};

const copyBoletoCode = async () => {
  const code = boletoModal.value?.codigo;
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
    boletoCopiado.value = true;
    setTimeout(() => {
      boletoCopiado.value = false;
    }, 2000);
  } catch {
    useAlert('Não foi possível copiar o código.');
  }
};

const loadHistorico = async () => {
  historicoLoading.value = true;
  loading.value = true;
  tabErrors.value.historico = '';
  try {
    syncFinanceiroRouteState();
    const params = Object.fromEntries(
      Object.entries(historyFilters.value).filter(([, value]) => value !== '')
    );
    const { data } = await FinanceiroAPI.historico(params);
    historico.value = {
      periodo: { label: '-' },
      filtros: {},
      cards: {
        saldo: null,
        receita: null,
        despesa: null,
        comissao: null,
      },
      serie_mensal: [],
      transacoes: [],
      historico_publico: [],
      atividades: [],
      resumo_publico: {
        total: 0,
        tickets: 0,
        feedback_suporte: 0,
        sessoes_responde: 0,
        visitas_publicas: 0,
        incidentes_status: 0,
      },
      ...data,
    };
  } catch {
    tabErrors.value.historico =
      'Não foi possível carregar o histórico financeiro.';
    useAlert('Não foi possível carregar o histórico financeiro.');
  } finally {
    historicoLoading.value = false;
    loading.value = false;
  }
};

const loadOffers = async () => {
  offerLoading.value = true;
  tabErrors.value.propostas = '';
  try {
    syncFinanceiroRouteState();
    const params = cleanParams({
      ...offerFilters.value,
      ...offerStatusParams(),
    });
    const { data } = await PropertyOffersAPI.get(params);
    offers.value = data.data || [];
    offerMeta.value = data.meta || { count: offers.value.length };
  } catch {
    tabErrors.value.propostas = 'Não foi possível carregar as propostas.';
    useAlert('Não foi possível carregar as propostas.');
    offers.value = [];
    offerMeta.value = { count: 0 };
  } finally {
    offerLoading.value = false;
  }
};

const loadLeases = async () => {
  leasesLoading.value = true;
  tabErrors.value.locacoes = '';
  try {
    const { data } = await LeasesAPI.list({ status: 'active', limit: 25 });
    leases.value = data.data || [];
    selectedLeaseId.value =
      leases.value.find(
        lease => String(lease.id) === String(route.query.lease_id || '')
      )?.id ||
      leases.value[0]?.id ||
      '';
  } catch {
    leases.value = [];
    selectedLeaseId.value = '';
    tabErrors.value.locacoes = 'Não foi possível carregar locações ativas.';
    useAlert('Não foi possível carregar locações ativas.');
  } finally {
    leasesLoading.value = false;
  }
};

const applyHistoryFilters = async () => {
  await loadHistorico();
};

const clearHistoryFilters = async () => {
  historyFilters.value = {
    start_date: '',
    end_date: '',
    transaction_kind: '',
    public_kind: '',
    q: '',
    limit: 50,
  };
  await applyHistoryFilters();
};

const updateHistoryFilter = ({ key, value }) => {
  if (!Object.prototype.hasOwnProperty.call(historyFilters.value, key)) return;
  historyFilters.value = {
    ...historyFilters.value,
    [key]: value,
  };
};

const openClosePeriod = () => {
  if (isPeriodClosed.value) return;

  showClosePeriodModal.value = true;
};

const closeClosePeriod = () => {
  showClosePeriodModal.value = false;
};

const confirmClosePeriod = async () => {
  closingPeriod.value = true;
  try {
    await FinanceiroAPI.fecharPeriodo({
      start_date:
        historico.value.periodo?.inicio || historyFilters.value.start_date,
      end_date: historico.value.periodo?.fim || historyFilters.value.end_date,
    });
    useAlert('Período financeiro fechado com sucesso.');
    showClosePeriodModal.value = false;
    await loadHistorico();
  } catch {
    useAlert('Não foi possível fechar este período financeiro.');
  } finally {
    closingPeriod.value = false;
  }
};

const applyOfferFilters = async () => {
  await loadOffers();
};

const clearOfferFilters = async () => {
  offerFilters.value = {
    business_context: '',
    kind: '',
    q: '',
  };
  await applyOfferFilters();
};

const changeOfferTab = async value => {
  offerTab.value = normalizeOfferTab(value);
  await applyOfferFilters();
};

const openOfferAction = (offer, action) => {
  const actionCopy = OFFER_ACTIONS[action];
  if (!actionCopy) return;

  pendingOfferAction.value = {
    action,
    offer,
    ...actionCopy,
  };
};

const closeOfferAction = () => {
  pendingOfferAction.value = null;
};

const confirmOfferAction = async () => {
  const actionCopy = pendingOfferAction.value;
  if (!actionCopy) return;

  offerLoading.value = true;
  try {
    await PropertyOffersAPI[actionCopy.action](
      actionCopy.offer.id,
      actionCopy.payload
    );
    useAlert(actionCopy.success);
    offerTab.value = actionCopy.nextTab;
    pendingOfferAction.value = null;
    syncFinanceiroRouteState();
    await loadOffers();
  } catch {
    useAlert('Ocorreu um erro. Tente novamente em alguns instantes.');
  } finally {
    offerLoading.value = false;
  }
};

const saveCreditAnalysis = async ({
  offer,
  result,
  monthlyIncome,
  checklist,
}) => {
  if (!offer?.id || !result?.valid) return;

  creditAnalysisLoading.value = true;
  try {
    await PropertyOffersAPI.updateCreditAnalysis(offer.id, {
      status: result.income_compatible === true ? 'pre_aprovado' : 'em_analise',
      provider: 'Simulador Imudei',
      approved_amount: result.financed_amount,
      monthly_income: monthlyIncome || null,
      income_commitment: result.income_commitment,
      income_compatible: result.income_compatible,
      checklist,
      simulation: result,
    });
    useAlert('Análise de crédito salva na proposta.');
    await loadOffers();
  } catch {
    useAlert('Não foi possível salvar a análise de crédito.');
  } finally {
    creditAnalysisLoading.value = false;
  }
};

const exportHistoryCsv = () => {
  const rows = [
    ['tipo', 'titulo', 'descricao', 'status', 'data', 'origem'],
    ...historico.value.historico_publico.map(item => [
      item.kind || '',
      item.title || '',
      item.description || '',
      item.status || '',
      item.date_label || item.occurred_at || '',
      item.source_surface || '',
    ]),
  ];
  const csv = rows
    .map(row =>
      row
        .map(value => `"${String(value ?? '').replaceAll('"', '""')}"`)
        .join(',')
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'financeiro-historico-publico.csv';
  link.click();
  URL.revokeObjectURL(url);
};

onMounted(async () => {
  loading.value = true;
  await Promise.all([
    loadResumo(),
    loadCobrancas(),
    loadCobranca(),
    loadHistorico(),
    loadOffers(),
    loadLeases(),
  ]);
  loading.value = false;
});
</script>

<template>
  <section
    class="flex h-full flex-col overflow-hidden bg-white font-redHatText"
  >
    <LaisHeader title="Financeiro" helper="Faça a gestão financeira da Lais." />

    <FinanceiroTabsShell v-model="tab" :tabs="FINANCEIRO_TABS" @change="setTab">
      <template #contratados>
        <FinanceiroContratadosPanel
          :loading="resumoLoading"
          :error="tabErrors.contratados"
          :proxima-cobranca="proximaCobranca"
          :plano-atual="planoAtual"
          :uso-ciclo="usoCiclo"
          :usage-origin-entries="usageOriginEntries"
          :invalid-reason-entries="invalidReasonEntries"
          :attendance-window-helper="attendanceWindowHelper"
          :format-b-r-l="formatBRL"
          :format-date-only="formatDateOnly"
          :trigger-kind-label="triggerKindLabel"
          :invalid-reason-label="invalidReasonLabel"
          @copy-code="copiarCodigo"
          @download-boleto="baixarBoleto"
          @retry="loadResumo"
        />
      </template>

      <template #cobranca>
        <FinanceiroCobrancaPanel
          :loading="cobrancaLoading"
          :error="tabErrors.cobranca"
          :cobranca="cobranca"
          :cobranca-sem-config="cobrancaSemConfig"
          :cobranca-ciclo="cobrancaCiclo"
          :boleto-loading="boletoConsultaLoading"
          :boleto-empty-notice="boletoEmptyNotice"
          :gauge-color="gaugeColor"
          :gauge-percent="gaugePercent"
          :attendance-window-helper="attendanceWindowHelper"
          :format-b-r-l="formatBRL"
          @update-cycle="setCiclo"
          @consult-boleto="consultBoletoAberto"
          @retry="loadCobranca"
        />
      </template>

      <template #boletos>
        <FinanceiroBoletosPanel
          :loading="cobrancasLoading"
          :error="tabErrors.boletos"
          :cobrancas="cobrancas"
          :format-b-r-l="formatBRL"
          :status-meta="statusMeta"
          @retry="loadCobrancas"
        />
      </template>

      <template #locacoes>
        <FinanceiroTabState
          :loading="leasesLoading"
          :error="tabErrors.locacoes"
          loading-label="Carregando locações..."
          @retry="loadLeases"
        >
          <div class="grid w-full gap-5">
            <article
              class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
            >
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2
                    class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
                  >
                    Administração de locações
                  </h2>
                  <p class="mt-1 max-w-2xl text-[13px] text-[#718096]">
                    Cobranças, vencimentos, inadimplência, repasses, reajustes,
                    vistorias, pendências e histórico de contratos ativos.
                  </p>
                </div>
                <span
                  class="rounded-full bg-[#f6f2fb] px-3 py-1 text-[12px] font-semibold text-[#49317a]"
                >
                  {{ leases.length }} ativa(s)
                </span>
              </div>

              <div
                v-if="leases.length"
                class="mt-4 flex gap-2 overflow-x-auto no-scrollbar"
              >
                <button
                  v-for="lease in leases"
                  :key="`finance-lease-${lease.id}`"
                  type="button"
                  class="whitespace-nowrap rounded-full border px-4 py-2 text-[13px] font-semibold transition"
                  :class="
                    String(selectedLeaseId) === String(lease.id)
                      ? 'border-[#49317a] bg-[#49317a] text-white'
                      : 'border-[#e5e7eb] bg-white text-[#4a5568] hover:border-[#cfc5e4]'
                  "
                  @click="selectLease(lease)"
                >
                  {{
                    lease.imovel_title ||
                    lease.reference ||
                    `Locação #${lease.id}`
                  }}
                </button>
              </div>
            </article>

            <div v-if="leasesLoading" class="py-20 text-center text-[#a0aec0]">
              Carregando locações...
            </div>
            <article
              v-else-if="!selectedLeaseId"
              class="rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-8 text-center"
            >
              <p class="text-[15px] font-semibold text-[#4a5568]">
                Nenhuma locação ativa encontrada
              </p>
              <p class="mt-1 text-[13px] text-[#a0aec0]">
                Contratos ativos aparecerão aqui quando forem materializados em
                locações.
              </p>
            </article>
            <LeaseOperationPanel
              v-else
              :lease-id="selectedLeaseId"
              title="Operação financeira da locação"
            />
          </div>
        </FinanceiroTabState>
      </template>

      <template #propostas>
        <FinanceiroTabState
          :loading="offerLoading"
          :error="tabErrors.propostas"
          loading-label="Carregando propostas..."
          @retry="loadOffers"
        >
          <div class="grid w-full gap-6">
            <article
              class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
            >
              <div
                class="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-[#f1f5f9] pb-4"
              >
                <div>
                  <h2
                    class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
                  >
                    {{ currentOfferBucket.title }}
                  </h2>
                  <p class="mt-1 max-w-2xl text-[13px] text-[#718096]">
                    {{ currentOfferBucket.helper }}
                  </p>
                </div>
                <span
                  class="rounded-full bg-[#f6f2fb] px-3 py-1 text-[12px] font-semibold text-[#49317a]"
                >
                  {{ offerSummary.count }} propostas
                </span>
              </div>

              <div class="mb-4 flex gap-2 overflow-x-auto no-scrollbar">
                <button
                  v-for="bucket in OFFER_BUCKETS"
                  :key="bucket.value"
                  type="button"
                  class="whitespace-nowrap rounded-full border px-4 py-2 text-[13px] font-semibold transition"
                  :class="
                    offerTab === bucket.value
                      ? 'border-[#49317a] bg-[#49317a] text-white'
                      : 'border-[#e5e7eb] bg-white text-[#4a5568] hover:border-[#cfc5e4]'
                  "
                  @click="changeOfferTab(bucket.value)"
                >
                  {{ bucket.label }}
                </button>
              </div>

              <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <label class="grid gap-1.5 text-[12px] text-[#718096]">
                  Buscar
                  <input
                    v-model="offerFilters.q"
                    class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
                    type="search"
                    placeholder="Imóvel, cliente, token..."
                  />
                </label>
                <label class="grid gap-1.5 text-[12px] text-[#718096]">
                  Finalidade
                  <select
                    v-model="offerFilters.business_context"
                    class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
                  >
                    <option value="">Todas</option>
                    <option value="purchase">Venda</option>
                    <option value="rental">Aluguel</option>
                  </select>
                </label>
                <label class="grid gap-1.5 text-[12px] text-[#718096]">
                  Tipo
                  <select
                    v-model="offerFilters.kind"
                    class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
                  >
                    <option value="">Todos</option>
                    <option value="offer">Proposta</option>
                    <option value="booking">Reserva</option>
                  </select>
                </label>
                <div class="flex items-end gap-3">
                  <button
                    type="button"
                    class="inline-flex h-10 flex-1 items-center justify-center rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white transition hover:bg-[#3d2966]"
                    @click="applyOfferFilters"
                  >
                    Aplicar
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-10 items-center justify-center rounded-lais border border-[#d6dce5] px-4 text-[13px] font-semibold text-[#4a5568] transition hover:bg-[#f8fafc]"
                    @click="clearOfferFilters"
                  >
                    Limpar
                  </button>
                </div>
              </div>
            </article>

            <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <article
                class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
              >
                <p
                  class="text-[12px] font-semibold uppercase tracking-wide text-[#a0aec0]"
                >
                  Propostas
                </p>
                <strong
                  class="font-redHatDisplay mt-3 block text-[28px] leading-none text-[#1a1a1a]"
                >
                  {{ offerSummary.count }}
                </strong>
                <p class="mt-2 text-[12px] text-[#718096]">
                  {{ currentOfferBucket.label }}
                </p>
              </article>
              <article
                class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
              >
                <p
                  class="text-[12px] font-semibold uppercase tracking-wide text-[#a0aec0]"
                >
                  Valor informado
                </p>
                <strong
                  class="font-redHatDisplay mt-3 block text-[28px] leading-none text-[#1a1a1a]"
                >
                  {{ formatBRL(offerSummary.amount) }}
                </strong>
                <p class="mt-2 text-[12px] text-[#718096]">
                  Soma das propostas exibidas
                </p>
              </article>
            </div>

            <FinancingSimulator
              :offers="offers"
              :is-saving="creditAnalysisLoading"
              @save-analysis="saveCreditAnalysis"
            />

            <article
              class="overflow-hidden rounded-lais border border-[#e5e7eb] bg-white shadow-lais-card"
            >
              <div
                class="flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f5f9] p-5"
              >
                <div>
                  <h2
                    class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]"
                  >
                    Lista de propostas
                  </h2>
                  <p class="mt-1 text-[13px] text-[#718096]">
                    Imóvel, proposta e status no mesmo acompanhamento.
                  </p>
                </div>
                <span
                  v-if="offerLoading"
                  class="text-[12px] font-semibold text-[#a0aec0]"
                >
                  Atualizando...
                </span>
              </div>

              <div
                v-if="!offerLoading && offers.length === 0"
                class="p-8 text-center text-[13px] text-[#718096]"
              >
                {{ currentOfferBucket.helper }}
              </div>

              <div v-else>
                <div class="hidden lg:block">
                  <table class="w-full text-[13px]">
                    <thead>
                      <tr
                        class="border-b border-[#e5e7eb] bg-[#fafafa] text-[11px] uppercase tracking-wide text-[#a0aec0]"
                      >
                        <th class="px-5 py-3 text-left font-semibold">
                          Imóvel
                        </th>
                        <th class="px-5 py-3 text-left font-semibold">
                          Proposta
                        </th>
                        <th class="px-5 py-3 text-left font-semibold">
                          Status
                        </th>
                        <th class="px-5 py-3 text-right font-semibold">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="offer in offers"
                        :key="offer.id"
                        class="border-b border-[#f3f4f6] align-top hover:bg-[#fafafa]"
                      >
                        <td class="px-5 py-4">
                          <p
                            class="font-redHatDisplay text-[16px] font-semibold text-[#1a1a1a]"
                          >
                            {{ offer.imovel_titulo || 'Imóvel sem título' }}
                          </p>
                          <p class="mt-1 text-[12px] text-[#718096]">
                            {{
                              offer.imovel_endereco || 'Endereço não informado'
                            }}
                          </p>
                          <p
                            class="mt-2 text-[11px] font-semibold text-[#a0aec0]"
                          >
                            {{ offerKindLabel(offer.kind) }} ·
                            {{ businessContextLabel(offer.business_context) }}
                          </p>
                        </td>
                        <td class="px-5 py-4">
                          <strong class="block text-[16px] text-[#1a1a1a]">
                            {{ formatBRL(offer.offer_amount) }}
                          </strong>
                          <p class="mt-1 text-[12px] text-[#718096]">
                            {{ paymentKindLabel(offer.payment_kind) }}
                          </p>
                          <p class="mt-2 text-[12px] text-[#a0aec0]">
                            {{ offerPersonLabel(offer) }}
                          </p>
                        </td>
                        <td class="px-5 py-4">
                          <span
                            class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                            :class="statusMeta(offer.status).cls"
                          >
                            {{ statusMeta(offer.status).label }}
                          </span>
                          <p class="mt-3 font-semibold text-[#1a1a1a]">
                            {{ offerProgressLabel(offer) }}
                          </p>
                          <p class="mt-1 max-w-xs text-[12px] text-[#718096]">
                            {{ offerNextStep(offer) }}
                          </p>
                          <p class="mt-2 text-[11px] text-[#a0aec0]">
                            {{ offerDateLabel(offer) }}
                          </p>
                          <div
                            v-if="hasCreditAnalysis(offer)"
                            class="mt-3 rounded-lais-sm bg-[#f8fafc] px-3 py-2 text-left"
                          >
                            <span
                              class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                              :class="creditStatusMeta(offer).cls"
                            >
                              {{ creditStatusMeta(offer).label }}
                            </span>
                            <p class="mt-1 text-[12px] text-[#718096]">
                              {{ creditIncomeLabel(offer) }}
                            </p>
                          </div>
                        </td>
                        <td class="px-5 py-4 text-right">
                          <div
                            v-if="offer.status === 'submitted'"
                            class="inline-flex flex-wrap justify-end gap-2"
                          >
                            <button
                              type="button"
                              class="inline-flex h-9 items-center justify-center rounded-lais bg-[#49317a] px-3 text-[12px] font-semibold text-white transition hover:bg-[#3d2966]"
                              @click="openOfferAction(offer, 'accept')"
                            >
                              Aceitar
                            </button>
                            <button
                              type="button"
                              class="inline-flex h-9 items-center justify-center rounded-lais border border-[#f2c4c4] px-3 text-[12px] font-semibold text-[#b42318] transition hover:bg-[#fff1f1]"
                              @click="openOfferAction(offer, 'decline')"
                            >
                              Recusar
                            </button>
                            <button
                              type="button"
                              class="inline-flex h-9 items-center justify-center rounded-lais border border-[#d6dce5] px-3 text-[12px] font-semibold text-[#4a5568] transition hover:bg-[#f8fafc]"
                              @click="openOfferAction(offer, 'withdraw')"
                            >
                              Retirar
                            </button>
                          </div>
                          <span v-else class="text-[12px] text-[#a0aec0]">
                            Sem ações pendentes
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="grid gap-3 p-4 lg:hidden">
                  <article
                    v-for="offer in offers"
                    :key="`mobile-offer-${offer.id}`"
                    class="rounded-lais border border-[#eef1f6] p-4"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <p
                          class="text-[11px] font-semibold uppercase text-[#a0aec0]"
                        >
                          {{ offerKindLabel(offer.kind) }} ·
                          {{ businessContextLabel(offer.business_context) }}
                        </p>
                        <h3
                          class="font-redHatDisplay mt-2 text-[18px] font-semibold text-[#1a1a1a]"
                        >
                          {{ offer.imovel_titulo || 'Imóvel sem título' }}
                        </h3>
                        <p class="mt-1 text-[12px] text-[#718096]">
                          {{
                            offer.imovel_endereco || 'Endereço não informado'
                          }}
                        </p>
                      </div>
                      <span
                        class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                        :class="statusMeta(offer.status).cls"
                      >
                        {{ statusMeta(offer.status).label }}
                      </span>
                    </div>

                    <div
                      class="mt-4 grid gap-3 rounded-lais-sm bg-[#faf8fd] p-3"
                    >
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-[12px] text-[#718096]">
                          Valor da proposta
                        </span>
                        <strong class="text-[15px] text-[#1a1a1a]">
                          {{ formatBRL(offer.offer_amount) }}
                        </strong>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-[12px] text-[#718096]">
                          Pagamento
                        </span>
                        <strong class="text-right text-[13px] text-[#1a1a1a]">
                          {{ paymentKindLabel(offer.payment_kind) }}
                        </strong>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-[12px] text-[#718096]">Cliente</span>
                        <strong class="text-right text-[13px] text-[#1a1a1a]">
                          {{ offerPersonLabel(offer) }}
                        </strong>
                      </div>
                    </div>

                    <p class="mt-4 font-semibold text-[#1a1a1a]">
                      {{ offerProgressLabel(offer) }}
                    </p>
                    <p class="mt-1 text-[13px] leading-relaxed text-[#718096]">
                      {{ offerNextStep(offer) }}
                    </p>
                    <p class="mt-2 text-[11px] text-[#a0aec0]">
                      {{ offerDateLabel(offer) }}
                    </p>
                    <div
                      v-if="hasCreditAnalysis(offer)"
                      class="mt-3 rounded-lais-sm bg-[#f8fafc] px-3 py-2"
                    >
                      <span
                        class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                        :class="creditStatusMeta(offer).cls"
                      >
                        {{ creditStatusMeta(offer).label }}
                      </span>
                      <p class="mt-1 text-[12px] text-[#718096]">
                        {{ creditIncomeLabel(offer) }}
                      </p>
                    </div>

                    <div
                      v-if="offer.status === 'submitted'"
                      class="mt-4 grid grid-cols-3 gap-2"
                    >
                      <button
                        type="button"
                        class="inline-flex h-10 items-center justify-center rounded-lais bg-[#49317a] px-2 text-[12px] font-semibold text-white"
                        @click="openOfferAction(offer, 'accept')"
                      >
                        Aceitar
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-10 items-center justify-center rounded-lais border border-[#f2c4c4] px-2 text-[12px] font-semibold text-[#b42318]"
                        @click="openOfferAction(offer, 'decline')"
                      >
                        Recusar
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-10 items-center justify-center rounded-lais border border-[#d6dce5] px-2 text-[12px] font-semibold text-[#4a5568]"
                        @click="openOfferAction(offer, 'withdraw')"
                      >
                        Retirar
                      </button>
                    </div>
                  </article>
                </div>
              </div>
            </article>
          </div>
        </FinanceiroTabState>
      </template>

      <template #comissoes>
        <ComissoesIndex />
      </template>

      <template #historico>
        <FinanceiroHistoricoPanel
          :loading="historicoLoading"
          :error="tabErrors.historico"
          :historico="historico"
          :history-filters="historyFilters"
          :history-card-copy="HISTORY_CARD_COPY"
          :is-period-closed="isPeriodClosed"
          :period-closed-copy="periodClosedCopy"
          :format-b-r-l="formatBRL"
          :format-date-only="formatDateOnly"
          :format-date-time="formatDateTime"
          :status-meta="statusMeta"
          :history-kind-meta="historyKindMeta"
          @apply-filters="applyHistoryFilters"
          @clear-filters="clearHistoryFilters"
          @export-csv="exportHistoryCsv"
          @filter-change="updateHistoryFilter"
          @open-close-period="openClosePeriod"
          @retry="loadHistorico"
        />
      </template>
    </FinanceiroTabsShell>

    <FinanceiroOfferActionModal
      :action="pendingOfferAction"
      :loading="offerLoading"
      :format-b-r-l="formatBRL"
      :payment-kind-label="paymentKindLabel"
      :business-context-label="businessContextLabel"
      :offer-kind-label="offerKindLabel"
      @close="closeOfferAction"
      @confirm="confirmOfferAction"
    />

    <FinanceiroClosePeriodModal
      :show="showClosePeriodModal"
      :historico="historico"
      :closing="closingPeriod"
      @close="closeClosePeriod"
      @confirm="confirmClosePeriod"
    />

    <FinanceiroBoletoModal
      :boleto="boletoModal"
      :copied="boletoCopiado"
      :format-b-r-l="formatBRL"
      @close="boletoModal = null"
      @copy-code="copyBoletoCode"
    />
  </section>
</template>
